'use client';

import { useEffect, useState } from 'react';
import { Cloud, CloudRain, Sun, Wind, Droplets, Sunrise, Sunset, CloudLightning, ThermometerSun, Umbrella } from 'lucide-react';
import { motion } from 'motion/react';

interface WeatherData {
  current: {
    temperature: number;
    feelsLike: number;
    humidity: number;
    windSpeed: number;
    condition: string;
    isDay: boolean;
    code: number;
  };
  daily: {
    sunrise: string;
    sunset: string;
    rainChance: number;
  };
}

const getWeatherInfo = (code: number, isDay: boolean = true) => {
  if (code === 0) return { text: 'Céu limpo', type: 'clear' };
  if (code === 1 || code === 2) return { text: 'Parcialmente nublado', type: 'cloudy' };
  if (code === 3) return { text: 'Nublado', type: 'cloudy' };
  if (code >= 45 && code <= 48) return { text: 'Neblina', type: 'cloudy' };
  if (code >= 51 && code <= 67) return { text: 'Chuva leve', type: 'rain' };
  if (code >= 71 && code <= 82) return { text: 'Chuva forte', type: 'rain' };
  if (code >= 95) return { text: 'Tempestade', type: 'storm' };
  return { text: 'Desconhecido', type: 'cloudy' };
};

const WeatherIcon = ({ type, isDay }: { type: string, isDay: boolean }) => {
  if (type === 'clear') {
    return isDay ? (
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      >
        <Sun className="w-16 h-16 text-amber-400 drop-shadow-sm" />
      </motion.div>
    ) : (
      <motion.div
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <Sun className="w-16 h-16 text-slate-400 drop-shadow-sm" />
      </motion.div>
    );
  }
  if (type === 'cloudy') {
    return (
      <motion.div
        animate={{ x: [-3, 3, -3] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        <Cloud className="w-16 h-16 text-slate-400 drop-shadow-sm" />
      </motion.div>
    );
  }
  if (type === 'rain') {
    return (
      <motion.div
        animate={{ y: [-1, 1, -1] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <CloudRain className="w-16 h-16 text-blue-400 drop-shadow-sm" />
      </motion.div>
    );
  }
  if (type === 'storm') {
    return (
      <motion.div
        animate={{ scale: [1, 1.03, 1] }}
        transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
      >
        <CloudLightning className="w-16 h-16 text-purple-400 drop-shadow-sm" />
      </motion.div>
    );
  }
  return <Cloud className="w-16 h-16 text-slate-400 drop-shadow-sm" />;
};

export default function WeatherWidget() {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchWeather() {
      try {
        const lat = -25.32;
        const lon = -53.15;
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,is_day,weather_code,wind_speed_10m&daily=sunrise,sunset,precipitation_probability_max&timezone=America%2FSao_Paulo`;
        
        const res = await fetch(url);
        const data = await res.json();
        
        const currentCode = data.current.weather_code;
        const isDay = data.current.is_day === 1;
        const { text: condition } = getWeatherInfo(currentCode, isDay);

        setWeather({
          current: {
            temperature: Math.round(data.current.temperature_2m),
            feelsLike: Math.round(data.current.apparent_temperature),
            humidity: data.current.relative_humidity_2m,
            windSpeed: Math.round(data.current.wind_speed_10m),
            condition,
            isDay,
            code: currentCode
          },
          daily: {
            sunrise: new Date(data.daily.sunrise[0]).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
            sunset: new Date(data.daily.sunset[0]).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
            rainChance: data.daily.precipitation_probability_max[0]
          }
        });
      } catch (error) {
        console.error('Failed to fetch weather', error);
      } finally {
        setLoading(false);
      }
    }

    fetchWeather();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse bg-white rounded-3xl shadow-lg border border-slate-200 p-8 h-96 flex flex-col items-center justify-center">
        <div className="w-24 h-24 bg-slate-200 rounded-full mb-4"></div>
        <div className="h-12 bg-slate-200 rounded w-32 mb-2"></div>
        <div className="h-6 bg-slate-200 rounded w-48 mb-8"></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 w-full">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 bg-slate-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!weather) return null;

  const { current, daily } = weather;
  const { type } = getWeatherInfo(current.code, current.isDay);

  // Dynamic background based on weather
  let bgGradient = 'from-sky-100/40 via-blue-200/40 to-blue-300/40';
  let textColor = 'text-slate-800';
  let mutedColor = 'text-slate-500';
  let glassBg = 'bg-white/30';
  let glassBorder = 'border-white/40';

  if (!current.isDay) {
    bgGradient = 'from-indigo-400/20 via-purple-500/20 to-slate-600/20';
    textColor = 'text-slate-900';
    mutedColor = 'text-slate-600';
    glassBg = 'bg-white/20';
    glassBorder = 'border-white/30';
  } else if (type === 'cloudy') {
    bgGradient = 'from-slate-100/40 via-slate-200/40 to-slate-300/40';
  } else if (type === 'rain' || type === 'storm') {
    bgGradient = 'from-blue-200/40 via-indigo-300/40 to-blue-400/40';
  }

  return (
    <div className={`rounded-3xl shadow-sm border ${glassBorder} backdrop-blur-2xl bg-gradient-to-br ${bgGradient} p-4 md:p-5 overflow-hidden relative transition-all duration-700 ${textColor}`}>
      
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
        {/* Main Weather Info (Left/Top) */}
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 scale-75 md:scale-90 origin-center">
            <WeatherIcon type={type} isDay={current.isDay} />
          </div>
          <div>
            <div className="flex items-start">
              <span className="text-4xl md:text-5xl font-bold tracking-tighter">
                {current.temperature}
              </span>
              <span className="text-xl md:text-2xl font-semibold mt-1 opacity-80">°C</span>
            </div>
            <p className="text-sm md:text-base font-medium capitalize opacity-90">
              {current.condition}
            </p>
          </div>
        </div>

        {/* Weather Details Grid (Right/Bottom) */}
        <div className="grid grid-cols-3 gap-2 md:gap-3 w-full md:w-auto">
          <div className={`${glassBg} backdrop-blur-md p-2 md:p-3 rounded-2xl border ${glassBorder} flex flex-col items-center text-center hover:bg-white/40 transition-colors min-w-[70px]`}>
            <ThermometerSun className="w-4 h-4 opacity-70 mb-1" />
            <p className={`text-[9px] ${mutedColor} uppercase tracking-wider font-bold mb-0.5`}>Sensação</p>
            <p className="font-bold text-sm">{current.feelsLike}°</p>
          </div>
          
          <div className={`${glassBg} backdrop-blur-md p-2 md:p-3 rounded-2xl border ${glassBorder} flex flex-col items-center text-center hover:bg-white/40 transition-colors min-w-[70px]`}>
            <Droplets className="w-4 h-4 opacity-70 mb-1" />
            <p className={`text-[9px] ${mutedColor} uppercase tracking-wider font-bold mb-0.5`}>Umidade</p>
            <p className="font-bold text-sm">{current.humidity}%</p>
          </div>
          
          <div className={`${glassBg} backdrop-blur-md p-2 md:p-3 rounded-2xl border ${glassBorder} flex flex-col items-center text-center hover:bg-white/40 transition-colors min-w-[70px]`}>
            <Wind className="w-4 h-4 opacity-70 mb-1" />
            <p className={`text-[9px] ${mutedColor} uppercase tracking-wider font-bold mb-0.5`}>Vento</p>
            <p className="font-bold text-sm">{current.windSpeed}</p>
          </div>
          
          <div className={`${glassBg} backdrop-blur-md p-2 md:p-3 rounded-2xl border ${glassBorder} flex flex-col items-center text-center hover:bg-white/40 transition-colors min-w-[70px]`}>
            <Umbrella className="w-4 h-4 opacity-70 mb-1" />
            <p className={`text-[9px] ${mutedColor} uppercase tracking-wider font-bold mb-0.5`}>Chuva</p>
            <p className="font-bold text-sm">{daily.rainChance}%</p>
          </div>

          <div className={`${glassBg} backdrop-blur-md p-2 md:p-3 rounded-2xl border ${glassBorder} flex flex-col items-center text-center hover:bg-white/40 transition-colors min-w-[70px]`}>
            <Sunrise className="w-4 h-4 text-amber-500/80 mb-1" />
            <p className={`text-[9px] ${mutedColor} uppercase tracking-wider font-bold mb-0.5`}>Nascer</p>
            <p className="font-bold text-sm">{daily.sunrise}</p>
          </div>

          <div className={`${glassBg} backdrop-blur-md p-2 md:p-3 rounded-2xl border ${glassBorder} flex flex-col items-center text-center hover:bg-white/40 transition-colors min-w-[70px]`}>
            <Sunset className="w-5 h-5 text-orange-500/80 mb-1" />
            <p className={`text-[9px] ${mutedColor} uppercase tracking-wider font-bold mb-0.5`}>Pôr do sol</p>
            <p className="font-bold text-sm">{daily.sunset}</p>
          </div>
        </div>
      </div>
      
    </div>
  );
}
